import React, { memo } from 'react';
import { Box, IconButton, Paper, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import styled from 'styled-components';
import { blue, orange, pink, lime, grey, deepPurple, red } from '@mui/material/colors';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import theme from '../theme';
import api from '../services/api';
import moment from 'moment';
import { Handle, Position } from 'react-flow-renderer';
import { CloseOutlined } from '@mui/icons-material';

const TIME_RANGES = [
  { label: '5m', seconds: 300 },
  { label: '15m', seconds: 900 },
  { label: '30m', seconds: 1800 },
  { label: '1h', seconds: 3600 },
  { label: '3h', seconds: 10800 },
];

const transformArray = (items) => {
  const groupedItems = items.reduce((result, item) => {
    const { itemid, clock, value } = item;
    const roundedClock = roundToNearestTime(clock);

    if (!result[roundedClock]) {
      result[roundedClock] = { clock: roundedClock };
    }

    result[roundedClock][itemid] = value;
    return result;
  }, {});

  return Object.values(groupedItems);
};

const findMinMaxClock = (items) => {
  const clockMap = {};

  for (const item of items) {
    for (const key in item) {
      if (key !== 'clock') {
        if (!clockMap[key]) {
          clockMap[key] = { minClock: item.clock, maxClock: item.clock };
        } else {
          if (item.clock < clockMap[key].minClock) {
            clockMap[key].minClock = item.clock;
          }
          if (item.clock > clockMap[key].maxClock) {
            clockMap[key].maxClock = item.clock;
          }
        }
      }
    }
  }

  let minClock = Number.MAX_SAFE_INTEGER;
  let maxClock = 0;

  for (const key in clockMap) {
    if (clockMap[key].minClock < minClock) {
      minClock = clockMap[key].minClock;
    }
    if (clockMap[key].maxClock > maxClock) {
      maxClock = clockMap[key].maxClock;
    }
  }

  return { minClock, maxClock };
};

const roundToNearestTime = (timestamp) => {
  const date = new Date(timestamp * 1000);
  const seconds = date.getSeconds();
  const roundedSeconds = Math.ceil(seconds / 60) * 60;
  date.setSeconds(roundedSeconds);
  date.setMilliseconds(0);

  if (roundedSeconds === 60) {
    date.setMinutes(date.getMinutes() + 1);
    date.setSeconds(0);
  }

  return Math.floor(date.getTime() / 1000);
};

const Node = styled.div`
  padding: 1px;
  border-radius: 5px;
  background: ${(props) => props.theme.nodeBg};
  color: ${(props) => props.theme.nodeColor};
  border: 0px solid ${(props) => (props.selected ? props.theme.primary : props.theme.nodeBorder)};

  .react-flow__handle {
    background: ${(props) => props.theme.primary};
    width: 0px;
    height: 0px;
    border: 0px;
  }

  .react-flow__handle {
    left: 15px;
    top: 0px;
  }
`;

const convertUnixToDateTime = (unixTimestamp) => moment.unix(unixTimestamp).format('DD-MM HH:mm');

function getScale(data) {
  let max = 0;
  for (const row of data) {
    for (const key in row) {
      if (key !== 'clock') {
        const v = Math.abs(Number(row[key]));
        if (v > max) max = v;
      }
    }
  }
  if (max >= 1e9) return { divisor: 1e9, unit: 'Gbps' };
  if (max >= 1e6) return { divisor: 1e6, unit: 'Mbps' };
  if (max >= 1e3) return { divisor: 1e3, unit: 'Kbps' };
  return { divisor: 1, unit: 'bps' };
}

function applyScale(data, divisor) {
  return data.map(row => {
    const scaled = { clock: row.clock };
    for (const key in row) {
      if (key !== 'clock') {
        scaled[key] = Number((Number(row[key]) / divisor).toFixed(2));
      }
    }
    return scaled;
  });
}

function filterByTimeRange(data, rangeSeconds) {
  if (!data || data.length === 0) return data;
  const now = Math.floor(Date.now() / 1000);
  const cutoff = now - rangeSeconds;
  return data.filter(item => item.clock >= cutoff);
}

const GraphNode = memo(({ data, selected }) => {
  const [graphData, setGraphData] = React.useState([]);
  const [timeRange, setTimeRange] = React.useState('3h');
  const listColor = [blue[900], orange[900], pink[900], lime[900], deepPurple[900], red[900]];

  const [edgesX, setEdgesX] = React.useState({});

  React.useEffect(() => {
    const fetchData = () => {
      const { cod_empresa, itemid } = data;
      api
        .post('map/items-data', {
          cod_empresa: cod_empresa,
          itemid: itemid,
        })
        .then((res) => {
          const tmp_data = res.data.map((item) => ({
            ...item,
            value: Number(item.value),
            clock: Number(item.clock)
          }));

          setGraphData(transformArray(tmp_data));
          setEdgesX(findMinMaxClock(tmp_data));
        });
    };

    fetchData();
    const intervalId = setInterval(fetchData, 60000);
    return () => clearInterval(intervalId);
  }, [data.itemid, data.display]);

  const rangeObj = TIME_RANGES.find(r => r.label === timeRange) || TIME_RANGES[4];
  const filteredData = filterByTimeRange(graphData, rangeObj.seconds);
  const filteredEdges = filteredData.length > 0 ? findMinMaxClock(filteredData) : edgesX;
  const scale = getScale(filteredData);
  const scaledData = applyScale(filteredData, scale.divisor);

  return (
    <Paper
      elevation={4}
      sx={{ color: theme.palette.mode === 'dark' ? '#ede7f6' : grey[900] }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, pt: 1 }}>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={(e, val) => { if (val) setTimeRange(val); }}
          size="small"
          sx={{ height: 28 }}
        >
          {TIME_RANGES.map(r => (
            <ToggleButton key={r.label} value={r.label} sx={{ fontSize: 11, px: 1, py: 0 }}>
              {r.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <IconButton
          sx={{ zIndex: 9999 }}
          onClick={() => data.close()}
        >
          <CloseOutlined />
        </IconButton>
      </Box>
      <Node selected={selected}>
        {scaledData.length > 0 && (
          <>
            {data.title?.map((title, i) =>
              <Typography key={i} variant="body" component={'div'} color={listColor[i]} style={{ textAlign: 'center', m: 2 }}>
                {title}
              </Typography>
            )}

            <AreaChart
              width={1200}
              height={300}
              data={scaledData}
              margin={{
                right: 50,
                left: 50,
                bottom: 20,
              }}
            >
              <CartesianGrid fillOpacity={0.6} />
              <XAxis
                dataKey="clock"
                tickFormatter={convertUnixToDateTime}
                allowDataOverflow={false}
                domain={[filteredEdges.minClock, filteredEdges.maxClock]}
              />
              <YAxis unit={scale.unit} />
              <defs>
                {listColor.map((color, i) => (
                  <linearGradient id={`${i}graph`} x1="0" y1="0" x2="0" y2="1" key={i}>
                    <stop offset="0%" stopColor={color} stopOpacity={0.8} />
                    <stop offset="100%" stopColor={color} stopOpacity={0.1} />
                  </linearGradient>
                ))}
              </defs>
              {data.itemid?.map((item, i) => (
                <Area
                  connectNulls
                  type="linear"
                  unit={scale.unit}
                  strokeWidth={2}
                  dataKey={item}
                  stackId={i}
                  name={data?.title[i]}
                  stroke={listColor[i]}
                  fill={`url(#${i}graph)`}
                  key={i}
                />
              ))}
            </AreaChart>
          </>
        )}
      </Node>
    </Paper>
  );
});

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        {payload?.map((v, i) =>
          <p key={i} className="label">{v.name} : {v.value}{v.unit}</p>
        )}
        <p className="label">{convertUnixToDateTime(payload[0].payload.clock)} </p>
      </div>
    );
  }
  return null;
};

export default GraphNode;
